
import * as React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import MainLayout from './components/MainLayout.js';
import ErrorPage from './components/error/ErrorPage.jsx';
import NotFoundPage from './components/notFound/NotFoundPage.jsx';
import LoginGuest from './features/Auth/components/LoginGuest/LoginGuest.js';
import LoginGuestEmail from './features/Auth/components/LoginGuest/LoginGuestEmail.js';
import LoadingScreen from './features/Auth/components/LoadingScreen/LoadingScreen.js';
import GlobalErrorBoundary from './utils/GlobalErrorBoundary.js';
import Administration from './features/Administration/Administration.jsx';
import MaxUsersReached from './features/Auth/components/MaxUsersReached/maxUsersReached.js';
import Statistics from './features/statistics/statistics.jsx';
import MonthlyReport from './features/statistics/monthlyReport/monthlyReport.jsx';

import { hasPermission, getSubDomain } from './features/Auth/utils/AuthUtils.js';
import { getGuestDemoLinkEncryptedSetting, getTenantByDomain, getGuestDemoLinkValidationSetting } from './features/Auth/store/authThunks';
import InscriptionConference from './features/ConferenceInscription/InscriptionConference.jsx';

// The root component that sets up the router
function App() {
  return (
    <GlobalErrorBoundary>
         <BrowserRouter>
            <AppContent />
          </BrowserRouter>
    </GlobalErrorBoundary>
  );
}

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, isMaxUsersReached } = useSelector((state) => state.auth);
  const isGuestRoute = location.pathname.startsWith('/guest' ) || location.pathname.startsWith('/conference/guest' ) ;
  const isInscriptionRoute = location.pathname.startsWith('/conference/registration');  
     const isConferenceGuestonRoute = window.location.pathname.startsWith('/conference/guest');  

  const dispatch = useDispatch();
  const isMonthlyReportRoute = location.pathname.startsWith('/monthlyReport');
  const urlParams = new URLSearchParams(location.search);
  const hasSecurityTokens = urlParams.has('s') && urlParams.has('t');
  const { guestDemoLinkEncryptedEnabled, guestDemoLinkWithValidationEnabled } = useSelector((state) => state.auth);
  const guestDemoSettingsLoaded =
    guestDemoLinkEncryptedEnabled !== null &&
    guestDemoLinkWithValidationEnabled !== null;


  React.useEffect(() => {
    const isGuestDemoRoute =
      location.pathname === "/guest/demo" ||
      location.pathname.startsWith("/guest/demo/")
      ;
    if (!isGuestDemoRoute) return;
    if (guestDemoSettingsLoaded) return;
    (async () => {
      try {
        const subDomain = getSubDomain();

        const tenantResp = await dispatch(
          getTenantByDomain({ params: [{ subDomain }] })
        ).unwrap();

        const tenantId = tenantResp?.tenant?.id;
        if (!tenantId) return;

        await Promise.all([
          dispatch(
            getGuestDemoLinkEncryptedSetting({ params: [{ tenantId }] })
          ).unwrap(),
          dispatch(
            getGuestDemoLinkValidationSetting({ params: [{ tenantId }] })
          ).unwrap(),
        ]);
      } catch (e) {
        console.error("init guest demo setting failed", e);
      }
    })();
  }, [location.pathname, guestDemoSettingsLoaded]);
  const canAccessPublicReport = isMonthlyReportRoute && hasSecurityTokens;
  return (
    <Routes>
   <Route path="/conference/registration/:code" element={<InscriptionConference />} />
      {/* Guest routes - available when not authenticated */}
 
      {(canAccessPublicReport) && (
        <Route path="/monthlyReport/:tenantId" element={<MonthlyReport />} />
      )}
      <Route path="/guest/:code" element={<LoginGuest />} />
       <Route path="/conference/guest/:code" element={<LoginGuest />} />
    
      {(
        !guestDemoSettingsLoaded
      ) && (
          <Route path="/guest/demo/*" element={<LoadingScreen />} />
        )}

      {guestDemoSettingsLoaded && guestDemoLinkEncryptedEnabled && guestDemoLinkWithValidationEnabled && (
        <>
          <Route path="/guest/demo" element={<LoginGuestEmail />} />
          <Route path="/guest/demo/:mapId" element={<NotFoundPage />} />
        </>
      )}

      {guestDemoSettingsLoaded && guestDemoLinkEncryptedEnabled && !guestDemoLinkWithValidationEnabled && (
        <>
          <Route path="/guest/demo" element={<LoginGuest />} />
          <Route path="/guest/demo/:mapId" element={<NotFoundPage />} />
        </>
      )}

      {guestDemoSettingsLoaded && !guestDemoLinkEncryptedEnabled && guestDemoLinkWithValidationEnabled && (
        <>
          <Route path="/guest/demo/:mapId" element={<LoginGuestEmail />} />
          <Route path="/guest/demo" element={<NotFoundPage />} />
        </>
      )}

      {guestDemoSettingsLoaded && !guestDemoLinkEncryptedEnabled && !guestDemoLinkWithValidationEnabled && (
        <>
          <Route path="/guest/demo/:mapId" element={<LoginGuest />} />
          <Route path="/guest/demo" element={<NotFoundPage />} />
        </>
      )}



      {/* Unauthenticated non-guest routes */}
      {!isAuthenticated && !isGuestRoute &&  !isInscriptionRoute && !isConferenceGuestonRoute &&  !isMaxUsersReached && !canAccessPublicReport  &&(
        <Route path="*" element={<LoadingScreen>Please log in to continue</LoadingScreen>} />
      )}

      {/* is Max Users Reached */}

      {isMaxUsersReached && !isAuthenticated && !isGuestRoute && (
        <Route path="*" element={<MaxUsersReached />} />
      )}
      {/* Authenticated routes */}
      {isAuthenticated && !isMaxUsersReached && (
        <>
          <Route path="/" element={<MainLayout />} />
          <Route path="/joinRoom/:mapId/:roomId" element={<MainLayout />} />
          <Route path="/ohno" element={<ErrorPage />} />
          {
             hasPermission("admin") && (<>
              <Route path="/monthlyReport" element={<MonthlyReport />} />
              <Route path="/admin" element={<Administration />} />
              <Route path="/statistics" element={<Statistics />} />

            </>)
          }
        </>
      )}

      {/* Catch-all for undefined routes */}
      {isAuthenticated && (
        <Route path="*" element={<NotFoundPage />} />
      )}
    </Routes>
  );
}

export default App;